import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { Objects, Surveys } from '@openforis/arena-core'

import * as Survey from '@core/survey/survey'
import * as NodeDef from '@core/survey/nodeDef'

import { useOnUpdate } from '@webapp/components/hooks'
import * as API from '@webapp/service/api'
import { useAuthCanUseAnalysis } from '@webapp/store/user'

import * as SurveyState from '../state'
import { SurveyStatusState } from '../status'

// ==== Survey
export const useSurveyDefsFetched = ({ draft, includeAnalysis, validate }) =>
  useSelector(SurveyStatusState.isFetchedWithSameParams({ draft, includeAnalysis, validate }))
export const useSurvey = () => useSelector(SurveyState.getSurvey)
export const useSurveyId = () => useSelector(SurveyState.getSurveyId)
export const useSurveyName = () => useSelector(SurveyState.getSurveyName)
export const useSurveyUuid = () => useSelector(SurveyState.getSurveyUuid)
export const useSurveyInfo = () => useSelector(SurveyState.getSurveyInfo)
export const useSurveySrsIndex = () =>
  useSelector((state) => {
    const surveyInfo = SurveyState.getSurveyInfo(state)
    return Survey.getSRSIndex(surveyInfo)
  }, Objects.isEqual)
export const useSurveyCycleKey = () => useSelector(SurveyState.getSurveyCycleKey)
export const useSurveyCycleKeys = () => useSelector(SurveyState.getSurveyCyclesKeys, Objects.isEqual)
export const useSurveyPreferredLang = () => useSelector(SurveyState.getSurveyPreferredLang)

export const useSurveyLangs = () => Survey.getLanguages(useSurveyInfo())

export const useOnSurveyCycleUpdate = (effect) => {
  const surveyCycleKey = useSurveyCycleKey()
  useOnUpdate(effect, [surveyCycleKey])
}

export const useNodeDefRootKeys = () => {
  const survey = useSurvey()
  const cycle = useSurveyCycleKey()
  const root = Survey.getNodeDefRoot(survey)
  return Survey.getNodeDefKeysSorted({ nodeDef: root, cycle })(survey)
}

export const useRootSummaryDefs = () => {
  const survey = useSurvey()
  const cycle = useSurveyCycleKey()
  return Survey.getRootSummaryDefs({ cycle })(survey)
}

export const useCategoryByName = (name) =>
  useSelector((state) => {
    if (!name) return null
    const survey = SurveyState.getSurvey(state)
    return Survey.getCategoryByName(name)(survey)
  })

export const useTaxonomies = () =>
  useSelector((state) => {
    const survey = SurveyState.getSurvey(state)
    return Survey.getTaxonomiesArray(survey)
  }, Objects.isEqual)

export const useTaxonomyByUuid = (uuid) =>
  useSelector((state) => {
    const survey = SurveyState.getSurvey(state)
    return Survey.getTaxonomyByUuid(uuid)(survey)
  })

export const useTaxonomyByName = (name) =>
  useSelector((state) => {
    if (!name) return null
    const survey = SurveyState.getSurvey(state)
    return Survey.getTaxonomyByName(name)(survey)
  })

// ==== Node defs
export const useNodeDefByUuid = (uuid) =>
  useSelector((state) => {
    if (!uuid) return null
    const survey = SurveyState.getSurvey(state)
    return Survey.getNodeDefByUuid(uuid)(survey)
  })

export const useNodeDefByName = (name) =>
  useSelector((state) => {
    if (!name) return null
    const survey = SurveyState.getSurvey(state)
    return Survey.findNodeDefByName(name)(survey)
  })

export const useNodeDefsByUuids = (uuids) =>
  useSelector((state) => {
    if (!uuids?.length) return []
    const survey = SurveyState.getSurvey(state)
    return Survey.getNodeDefsByUuids(uuids)(survey)
  }, Objects.isEqual)

export const useNodeDefsByNames = (names) =>
  useSelector((state) => {
    if (!names?.length) return []
    const survey = SurveyState.getSurvey(state)
    return names.map((name) => Survey.findNodeDefByName(name)(survey)).filter(Boolean)
  }, Objects.isEqual)

export const useIsAncestorMultipleEntityRoot = (nodeDef) =>
  useSelector((state) => {
    const survey = SurveyState.getSurvey(state)
    const rootDef = Survey.getNodeDefRoot(survey)
    const ancestorMultipleEntityDef = Survey.getNodeDefAncestorMultipleEntity(nodeDef)(survey)
    return NodeDef.isEqual(rootDef)(ancestorMultipleEntityDef)
  })

export const useIsNodeDefEnumerator = (nodeDef) =>
  useSelector((state) => {
    if (!NodeDef.isCode(nodeDef)) return false
    const survey = SurveyState.getSurvey(state)
    return Surveys.isNodeDefEnumerator({ survey, nodeDef })
  })

export const useNodeDefLabel = (nodeDef, type) => NodeDef.getLabel(nodeDef, useSurveyPreferredLang(), type)

export const useNodeDefValidationByUuid = (uuid) =>
  useSelector((state) => {
    const survey = SurveyState.getSurvey(state)
    const nodeDef = Survey.getNodeDefByUuid(uuid)(survey)
    return Survey.getNodeDefValidation(nodeDef)(survey)
  }, Objects.isEqual)

export const useSurveyHasFileAttributes = () =>
  useSelector((state) => {
    const survey = SurveyState.getSurvey(state)
    const fileDefs = Survey.findDescendants({ filterFn: NodeDef.isFile })(survey)
    return fileDefs.length > 0
  })

export const useIsSurveyDirty = () => useSelector(SurveyStatusState.isDirty)

export const useChains = ({ surveyCycleKey } = {}) => {
  const canUseAnalysis = useAuthCanUseAnalysis()
  const surveyId = useSurveyId()
  const [chains, setChains] = useState(null)

  useEffect(() => {
    let isMounted = true
    if (canUseAnalysis) {
      const fetchChains = async () => {
        const { chains: _chains } = await API.fetchChains({ surveyId, surveyCycleKey })
        if (isMounted) {
          setChains(_chains)
        }
      }
      void fetchChains()
    }
    return () => {
      isMounted = false
    }
  }, [canUseAnalysis, surveyId, surveyCycleKey])
  return canUseAnalysis ? chains : null
}
